/**
 * The billing surface — balance/tier/subscription, the plan + top-up catalogs as
 * REAL purchase actions, a cancel affordance, and a recent statement. The api is
 * the sole authority on every number here (CLAUDE.md: cards/modals are
 * display-only for DATA); this component formats them and hands a tapped plan or
 * pack to the store, which opens the rail-hosted (CCBill) checkout.
 *
 * Nothing is granted client-side: a purchase is a NAVIGATION to the rail, and the
 * verified webhook applies the credits — so the buttons never mutate the balance
 * they render. Loads once on mount via the injected {@link BillingStore}.
 */
import { Button, Spinner, useT } from '@4f/ui';
import { createMemo, For, type JSX, onMount, Show } from 'solid-js';
import type { BillingSummaryView, LedgerEntryView, PackView, PlanView } from '../../api/types.ts';
import type { BillingPending, BillingStore } from '../../stores/billing.ts';
import {
  canCancelSubscription,
  formatDelta,
  formatUsd,
  isPurchasablePlan,
  microsToCredits,
  planPriceUsd,
  sortPlans,
} from './billing-model.ts';

export interface BillingSurfaceProps {
  readonly store: BillingStore;
}

export function BillingSurface(props: BillingSurfaceProps): JSX.Element {
  const { t } = useT();

  onMount(() => {
    void props.store.load();
  });

  const plans = createMemo(() => sortPlans(props.store.summary()?.plans ?? []));
  const pending = (): BillingPending | null => props.store.pending();
  const busy = (kind: BillingPending['kind'], id: string): boolean => {
    const current = pending();
    return current?.kind === kind && current.id === id;
  };

  return (
    <div class="f4-billing">
      <Show when={props.store.loading() && !props.store.summary()}>
        <Spinner label={t('billing.title')} />
      </Show>
      <Show when={props.store.error()}>
        {(error) => (
          <p class="f4-genui__error" role="alert">
            {error().message}
          </p>
        )}
      </Show>
      <Show when={props.store.summary()}>
        {(summary) => (
          <>
            <BalanceCard
              summary={summary()}
              canceling={busy('cancel', 'subscription')}
              blocked={pending() !== null}
              onCancel={() => void props.store.cancelSubscription()}
            />
            <PlansSection
              plans={plans()}
              busy={(tier) => busy('plan', tier)}
              blocked={pending() !== null}
              onSubscribe={(tier) => void props.store.subscribe(tier)}
            />
            <PacksSection
              packs={summary().packs}
              busy={(packId) => busy('pack', packId)}
              blocked={pending() !== null}
              onBuy={(packId) => void props.store.buyPack(packId)}
            />
            {/* Discreet-descriptor promise, shown wherever money can be spent. */}
            <p class="f4-billing__note">{t('billing.purchase.descriptorNote')}</p>
            <Show when={pending()?.kind !== 'cancel' && pending() !== null}>
              <p class="f4-billing__note" role="status">
                {t('billing.purchase.checkoutRedirect')}
              </p>
            </Show>
            <StatementSection entries={summary().recentLedger} />
          </>
        )}
      </Show>
    </div>
  );
}

interface BalanceCardProps {
  readonly summary: BillingSummaryView;
  readonly canceling: boolean;
  readonly blocked: boolean;
  onCancel(): void;
}

function BalanceCard(props: BalanceCardProps): JSX.Element {
  const { t } = useT();
  const credits = createMemo(() => microsToCredits(props.summary.creditBalanceMicros));
  return (
    <section
      class="f4-billing__balance"
      aria-label={t('billing.balance.credits', { count: credits() })}
    >
      <p class="f4-billing__credits">{t('billing.balance.credits', { count: credits() })}</p>
      <p class="f4-billing__tier">
        {t('billing.balance.tier', { tier: t(`billing.tiers.${props.summary.tier}`) })}
      </p>
      <Show when={props.summary.subscription}>
        {(subscription) => (
          <>
            <p class="f4-billing__subscription">
              {t(`billing.subscriptionStatus.${subscription().status}`)}
            </p>
            {/* Consumer protection: what can be bought in-app is cancellable in-app. */}
            <Show when={canCancelSubscription(subscription())}>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                class="f4-billing__cancel"
                disabled={props.blocked}
                loading={props.canceling}
                loadingLabel={t('surface.working')}
                onClick={() => props.onCancel()}
              >
                {t('billing.plans.cancel')}
              </Button>
            </Show>
          </>
        )}
      </Show>
    </section>
  );
}

interface PlansSectionProps {
  readonly plans: readonly PlanView[];
  readonly blocked: boolean;
  busy(tier: string): boolean;
  onSubscribe(tier: PlanView['tier']): void;
}

/** The plan ladder — each paid, non-current tier is a real subscribe action. */
function PlansSection(props: PlansSectionProps): JSX.Element {
  const { t } = useT();
  return (
    <section class="f4-billing__plans" aria-label={t('billing.plans.title')}>
      <h3 class="f4-billing__sectionTitle">{t('billing.plans.title')}</h3>
      <ul class="f4-billing__planList">
        <For each={props.plans}>
          {(plan) => (
            <li class="f4-billing__planCard" aria-current={plan.current ? 'true' : undefined}>
              <span class="f4-billing__planTier">{t(`billing.tiers.${plan.tier}`)}</span>
              <span class="f4-billing__planCredits">
                {t('billing.balance.credits', {
                  count: microsToCredits(plan.monthlyCreditsMicros),
                })}
              </span>
              <Show when={isPurchasablePlan(plan)}>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  class="f4-billing__buy"
                  disabled={props.blocked}
                  loading={props.busy(plan.tier)}
                  loadingLabel={t('surface.working')}
                  onClick={() => props.onSubscribe(plan.tier)}
                >
                  {t('surface.subscribeFor', {
                    price: t('billing.packs.price', { price: formatUsd(planPriceUsd(plan.tier)) }),
                  })}
                </Button>
              </Show>
            </li>
          )}
        </For>
      </ul>
    </section>
  );
}

interface PacksSectionProps {
  readonly packs: readonly PackView[];
  readonly blocked: boolean;
  busy(packId: string): boolean;
  onBuy(packId: string): void;
}

/** The top-up pack catalog — price + credit grant, each a real purchase action. */
function PacksSection(props: PacksSectionProps): JSX.Element {
  const { t } = useT();
  return (
    <Show when={props.packs.length > 0}>
      <section class="f4-billing__packs" aria-label={t('billing.packs.title')}>
        <h3 class="f4-billing__sectionTitle">{t('billing.packs.title')}</h3>
        <ul class="f4-billing__planList">
          <For each={props.packs}>
            {(pack) => (
              <li class="f4-billing__planCard">
                <span class="f4-billing__planTier">
                  {t('billing.packs.price', { price: formatUsd(pack.priceUsd) })}
                </span>
                <span class="f4-billing__planCredits">
                  {t('billing.balance.credits', { count: microsToCredits(pack.creditsMicros) })}
                </span>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  class="f4-billing__buy"
                  disabled={props.blocked}
                  loading={props.busy(pack.id)}
                  loadingLabel={t('surface.working')}
                  onClick={() => props.onBuy(pack.id)}
                >
                  {t('billing.purchase.buyCredits')}
                </Button>
              </li>
            )}
          </For>
        </ul>
      </section>
    </Show>
  );
}

function StatementSection(props: { readonly entries: readonly LedgerEntryView[] }): JSX.Element {
  const { t } = useT();
  return (
    <section class="f4-billing__statement" aria-label={t('billing.statement.title')}>
      <h3 class="f4-billing__sectionTitle">{t('billing.statement.title')}</h3>
      <Show
        when={props.entries.length > 0}
        fallback={<p class="f4-billing__empty">{t('billing.statement.empty')}</p>}
      >
        <ul class="f4-billing__ledgerList">
          <For each={props.entries}>
            {(entry) => (
              <li class="f4-billing__ledgerRow">
                <span class="f4-billing__ledgerReason">
                  {t(`billing.ledgerReason.${entry.reason}`)}
                </span>
                <span class="f4-billing__ledgerDelta">
                  {t('billing.statement.credits', { amount: formatDelta(entry.deltaMicros) })}
                </span>
              </li>
            )}
          </For>
        </ul>
      </Show>
    </section>
  );
}
